import { useState } from 'react'
import { Cloud, Loader2, AlertCircle, CheckCircle, X, Unplug } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card } from '@/components/ui/card'
import { useCfConnectionStatus, useCfConnectionStore, useCfConnectionDisconnect } from '@/hooks/useApi'

export function CloudflareConnectionPanel() {
  const { data: status, isLoading, refetch } = useCfConnectionStatus()
  const storeMutation = useCfConnectionStore()
  const disconnectMutation = useCfConnectionDisconnect()

  const [accountId, setAccountId] = useState('')
  const [apiToken, setApiToken] = useState('')
  const [formError, setFormError] = useState('')
  const [showForm, setShowForm] = useState(false)

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)
  const [disconnectInput, setDisconnectInput] = useState('')
  const [disconnectError, setDisconnectError] = useState('')

  const connected = status?.connected ?? false
  const accountLast4 = status?.account_id ? status.account_id.slice(-4) : ''
  const disconnectMatches = accountLast4.length === 4 && disconnectInput.trim() === accountLast4

  const handleStore = async () => {
    setFormError('')
    if (!accountId.trim()) { setFormError('Account ID required'); return }
    if (!apiToken.trim()) { setFormError('API token required'); return }
    try {
      await storeMutation.mutateAsync({ account_id: accountId.trim(), api_token: apiToken.trim() })
      setAccountId('')
      setApiToken('')
      setShowForm(false)
      refetch()
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to store token')
    }
  }

  const handleDisconnect = async () => {
    setDisconnectError('')
    if (!disconnectMatches) { setDisconnectError('Digits do not match the connected account ID'); return }
    try {
      await disconnectMutation.mutateAsync()
      setShowDisconnectConfirm(false)
      setDisconnectInput('')
      refetch()
    } catch (e) {
      setDisconnectError(e instanceof Error ? e.message : 'Failed to disconnect')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Cloudflare Connection</h2>
        <p className="text-sm text-text-muted mt-0.5">
          Connect a second Cloudflare account to enable cross-account operations.
        </p>
      </div>

      <Card className="p-4 bg-surface/30 border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cloud className="h-5 w-5 text-text-muted" />
            <div>
              <div className="flex items-center gap-2">
                {connected ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-success" />
                    <span className="text-sm font-medium text-text-primary">
                      {status?.account_id ?? 'Connected'}
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4 text-text-muted" />
                    <span className="text-sm font-medium text-text-muted">Not connected</span>
                  </>
                )}
              </div>
              {connected && status?.worker_count !== undefined && (
                <p className="text-xs text-text-muted mt-0.5">
                  {status.worker_count} worker{status.worker_count === 1 ? '' : 's'} accessible
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-wrap justify-end">
            <Button
              size="sm"
              variant="ghost"
              className="text-text-muted hover:text-text-primary"
              onClick={() => setShowForm(v => !v)}
            >
              {connected ? 'Replace token' : 'Connect'}
            </Button>
            {connected && (
              <Button
                size="sm"
                variant="ghost"
                className="text-danger hover:text-danger hover:bg-danger/10"
                onClick={() => { setShowDisconnectConfirm(v => !v); setDisconnectError(''); setDisconnectInput('') }}
              >
                <Unplug className="h-3.5 w-3.5 mr-1.5" />
                Disconnect
              </Button>
            )}
          </div>
        </div>

        {connected && status?.worker_names && status.worker_names.length > 0 && (
          <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
            {status.worker_names.map((name: string) => (
              <div key={name} className="flex items-center gap-2 py-1 px-2 rounded bg-surface/20 border border-border/40">
                <Cloud className="h-3 w-3 text-text-muted shrink-0" />
                <span className="text-xs text-text-primary font-mono">{name}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {showDisconnectConfirm && connected && (
        <Card className="p-4 bg-danger/5 border-danger/30 space-y-3">
          <div className="flex items-start gap-2 text-xs text-danger">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              This permanently removes the stored connection to account{' '}
              <span className="font-mono">{status?.account_id}</span>. Type the last 4 digits
              of the account ID above to confirm.
            </span>
          </div>
          <Input
            placeholder="Last 4 digits of account ID"
            value={disconnectInput}
            onChange={e => setDisconnectInput(e.target.value)}
            maxLength={4}
            className="bg-bg border-border text-text-primary font-mono text-sm"
          />
          {disconnectError && (
            <p className="text-xs text-danger flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />{disconnectError}
            </p>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDisconnect}
              disabled={!disconnectMatches || disconnectMutation.isPending}
            >
              {disconnectMutation.isPending
                ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                : <Unplug className="h-3.5 w-3.5 mr-1.5" />}
              Confirm Disconnect
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setShowDisconnectConfirm(false); setDisconnectError(''); setDisconnectInput('') }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </Card>
      )}

      {showForm && (
        <Card className="p-4 bg-surface/30 border-border space-y-3">
          <div className="flex items-start gap-2 text-xs text-warning">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>Token is encrypted at rest (AES-256-GCM) and never returned after storage.</span>
          </div>
          <Input
            placeholder="Cloudflare Account ID"
            value={accountId}
            onChange={e => setAccountId(e.target.value)}
            className="bg-bg border-border text-text-primary font-mono text-sm"
          />
          <Input
            type="password"
            placeholder="API Token"
            value={apiToken}
            onChange={e => setApiToken(e.target.value)}
            className="bg-bg border-border text-text-primary font-mono text-sm"
          />
          {formError && (
            <p className="text-xs text-danger flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />{formError}
            </p>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={handleStore} disabled={storeMutation.isPending}>
              {storeMutation.isPending
                ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                : <CheckCircle className="h-3.5 w-3.5 mr-1.5" />}
              Store & Connect
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setFormError('') }}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}
